import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { BrowserMultiFormatReader } from "@zxing/browser";

export interface FrontendTebligatReadableField {
    key: string;
    label: string;
    value: string;
}

export interface FrontendTebligatPageResult {
    pageNumber: number;
    text: string;
    barkodNo: string | null;
    ocrText: string | null;
}

export interface FrontendTebligatParseOptions {
    maxPages?: number;
    renderScale?: number;
    enableBarcodeScan?: boolean;
    enableOcr?: boolean;
    ocrLanguages?: string;
    maxOcrPages?: number;
}

export interface FrontendTebligatParseResult {
    data: {
        barkodNo: string | null;
        adSoyad: string | null;
        kurumAd: string;
        adres: string | null;
        icerik: string | null;
        dosyaEsasNo: string | null;
        birimAdi: string | null;
        isETebligat: boolean;
        isKurum: boolean;
    };
    status: boolean;
    errors: string[];
}

export default class FrontendTebligatPdfParser {
    private static isWorkerConfigured: boolean = false;
    private readonly barcodeReader: BrowserMultiFormatReader;

    constructor() {
        this.barcodeReader = new BrowserMultiFormatReader();
    }

    async parse(blob: Blob, options: FrontendTebligatParseOptions = {}): Promise<FrontendTebligatParseResult> {
        if (!(blob instanceof Blob)) {
            throw new TypeError("Parametre Blob olmalıdır.");
        }
        const errors: string[] = [];

        const maxPages = options.maxPages ?? 5;
        const renderScale = options.renderScale ?? 2.2;
        const enableBarcodeScan = options.enableBarcodeScan !== false;
        const enableOcr = options.enableOcr === true;
        const ocrLanguages = options.ocrLanguages ?? "tur+eng";
        const maxOcrPages = options.maxOcrPages ?? 2;

        FrontendTebligatPdfParser.ensurePdfWorkerConfigured();

        const buffer = await blob.arrayBuffer();
        let pdf: any;
        try {
            const loadingTask = (pdfjsLib as any).getDocument({
                data: buffer
            });
            pdf = await loadingTask.promise;
        } catch (workerError) {
            console.warn("pdf.js worker açılamadı, disableWorker fallback kullanılacak.", workerError);
            errors.push("pdf.js worker açılamadı, disableWorker fallback kullanıldı.");
            const loadingTask = (pdfjsLib as any).getDocument({
                data: buffer,
                disableWorker: true
            });
            pdf = await loadingTask.promise;
        }

        const pageCount = Math.min(pdf.numPages, maxPages);
        const textLines: string[] = [];
        const pages: FrontendTebligatPageResult[] = [];

        let firstBarcode: string | null = null;
        let anyOcrUsed = false;
        let anyBarcodeUsed = false;

        for (let i = 1; i <= pageCount; i++) {
            const page = await pdf.getPage(i);
            const pageText = await this.extractPageText(page);
            this.pushLines(textLines, pageText);

            let barkodNo: string | null = this.extractBarcode(pageText);
            let ocrText: string | null = null;

            let canvas: HTMLCanvasElement | null = null;
            if (enableBarcodeScan || (enableOcr && i <= maxOcrPages && this.isWeakText(pageText))) {
                canvas = await this.renderPage(page, renderScale);
            }

            if (!barkodNo && enableBarcodeScan && canvas) {
                barkodNo = await this.decodeBarcodeFromCanvas(canvas);
                if (barkodNo) {
                    anyBarcodeUsed = true;
                }
            }

            if (!barkodNo && enableOcr && canvas && i <= maxOcrPages && this.isWeakText(pageText)) {
                ocrText = await this.extractTextWithOcr(canvas, ocrLanguages);
                if (ocrText) {
                    anyOcrUsed = true;
                    this.pushLines(textLines, ocrText);
                    barkodNo = this.extractBarcode(ocrText);
                }
            }

            if (!firstBarcode && barkodNo) {
                firstBarcode = barkodNo;
            }

            pages.push({
                pageNumber: i,
                text: pageText,
                barkodNo,
                ocrText
            });
        }

        const rawText = textLines.join("\n");
        const barkodNo = firstBarcode || this.extractBarcode(rawText);
        if (!barkodNo) {
            errors.push("Barkod bulunamadı.");
        }
        if (anyOcrUsed) {
            errors.push("OCR fallback kullanıldı.");
        } else if (anyBarcodeUsed) {
            errors.push("Barkod görüntüden (ZXing) okundu.");
        }
        const isETebligat = !!(barkodNo && barkodNo.startsWith("5"));
        const adSoyad = isETebligat ? this.findAdSoyadForETebligat(rawText) : this.findAdSoyad(rawText);
        const kurumAd = isETebligat ? "" : (this.findKurumAdSoyad(rawText) || "");
        const adres = isETebligat ? null : this.findAdres(rawText);
        const icerik = this.findTebligatIcerigi(rawText);
        const dosyaEsasNo = this.findDosyaEsasNo(rawText);
        const birimAdi = this.findBirimAdi(rawText);
        const isKurum = isETebligat ? false : this.detectKurum(adSoyad, kurumAd);

        return {
            data: {
                barkodNo,
                adSoyad,
                kurumAd,
                adres,
                icerik,
                dosyaEsasNo,
                birimAdi,
                isETebligat,
                isKurum
            },
            status: true,
            errors
        };
    }

    private async extractPageText(page: any): Promise<string> {
        try {
            const textContent = await page.getTextContent();
            const chunks = textContent.items
                .map((item: any) => (typeof item.str === "string" ? item.str : ""))
                .filter((x: string) => !!x.trim())
                .map((x: string) => this.cleanText(x));
            return chunks.join(" ").trim();
        } catch {
            return "";
        }
    }

    private async renderPage(page: any, scale: number): Promise<HTMLCanvasElement> {
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        if (!context) {
            throw new Error("Canvas 2D context oluşturulamadı.");
        }

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        await page.render({
            canvasContext: context,
            viewport
        }).promise;

        return canvas;
    }

    private async decodeBarcodeFromCanvas(canvas: HTMLCanvasElement): Promise<string | null> {
        try {
            const result: any = await this.barcodeReader.decodeFromCanvas(canvas);
            const text = result?.getText ? result.getText() : result?.text;
            if (typeof text === "string" && text.trim()) {
                return text.trim();
            }
            return null;
        } catch {
            return null;
        }
    }

    private async extractTextWithOcr(canvas: HTMLCanvasElement, languages: string): Promise<string | null> {
        try {
            const tesseract = await import("tesseract.js");
            const response: any = await tesseract.recognize(canvas, languages);
            const text = response?.data?.text;
            if (typeof text === "string" && text.trim()) {
                return this.cleanText(text);
            }
            return null;
        } catch {
            return null;
        }
    }

    private extractBarcode(text: string): string | null {
        const patterns = [
            /barkod[^0-9]{0,15}([0-9]{10,30})/i,
            /\*([0-9A-Z\-. $/+%]{8,40})\*/i,
            /\b([0-9]{13,30})\b/
        ];

        for (const pattern of patterns) {
            const match = text.match(pattern);
            if (match?.[1]) {
                const value = match[1].replace(/\s+/g, "").trim();
                if (value.length >= 8) {
                    return value;
                }
            }
        }
        return null;
    }

    private extractByLabel(text: string, labels: string[]): string | null {
        const lines = text.split(/\n+/).map((x) => x.trim()).filter(Boolean);
        for (const line of lines) {
            const low = line.toLocaleLowerCase("tr-TR");
            for (const label of labels) {
                const idx = low.indexOf(label);
                if (idx >= 0) {
                    const parts = line.split(/[:\-]/);
                    if (parts.length >= 2) {
                        const candidate = this.cleanText(parts.slice(1).join(" "));
                        if (candidate.length >= 2) {
                            return candidate;
                        }
                    }
                    const afterLabel = this.cleanText(line.slice(idx + label.length));
                    if (afterLabel.length >= 2) {
                        return afterLabel;
                    }
                }
            }
        }
        return null;
    }

    private createReadableFields(values: {
        barkodNo: string | null;
        adSoyad: string | null;
        kurumAd: string | null;
        isKurum: boolean | null;
    }): FrontendTebligatReadableField[] {
        const out: FrontendTebligatReadableField[] = [];

        if (values.barkodNo) out.push({ key: "barkodNo", label: "Barkod No", value: values.barkodNo });
        if (values.adSoyad) out.push({ key: "adSoyad", label: "Ad Soyad", value: values.adSoyad });
        if (values.kurumAd) out.push({ key: "kurumAd", label: "Kurum Adı", value: values.kurumAd });
        if (values.isKurum !== null) out.push({ key: "isKurum", label: "Kurum Mu", value: values.isKurum ? "Evet" : "Hayır" });

        return out;
    }

    private findAdSoyad(rawText: string): string | null {
        const normalized = this.normalizeText(rawText);
        const fromAdiSoyadi = this.findValueAfterLabel(normalized.text, [
            "adı ve soyadı",
            "adi ve soyadi",
            "adı soyadı",
            "adi soyadi"
        ], { maxNextLines: 2 });

        const value = fromAdiSoyadi || this.extractByLabel(normalized.text, ["muhatap", "borçlu", "borclu"]);
        const cleaned = this.cleanPersonName(value);
        if (cleaned) return cleaned;

        const candidates = this.extractNameCandidates(normalized.text);
        return this.pickBestName(candidates);
    }

    private findAdSoyadForETebligat(rawText: string): string | null {
        const normalized = this.normalizeText(rawText);
        const borcluRegex = /\b(?:borçlu|borclu)\s+([A-ZÇĞİÖŞÜa-zçğıöşü\s']{5,})/iu;
        const borcluMatch = rawText.match(borcluRegex);
        if (borcluMatch?.[1]) {
            const tail = borcluMatch[1];
            const cut = tail.split(/\b(vekili|vekil|uets|mühür|muhur|imza|dosya|icra|e-?tebligat)\b/i)[0];
            const direct = this.cleanText(cut);
            if (direct.split(/\s+/).length >= 2) return direct;
            const cleaned = this.cleanPersonName(cut);
            if (cleaned) return cleaned;
        }

        const fromBorclu = this.extractByLabel(normalized.text, ["borçlu", "borclu"]);
        if (fromBorclu) {
            const direct = this.cleanText(fromBorclu);
            if (direct.split(/\s+/).length >= 2) return direct;
        }
        const cleanedBorclu = this.cleanPersonName(fromBorclu);
        if (cleanedBorclu) return cleanedBorclu;

        const fromAdiSoyadi = this.findValueAfterLabel(rawText, ["adı ve soyadı", "adi ve soyadi"], { maxNextLines: 2 });
        if (fromAdiSoyadi) {
            const vekilMatch = fromAdiSoyadi.match(/vekili\s+av\.?\s+(.+)$/i);
            if (vekilMatch?.[1]) {
                return this.cleanPersonName(vekilMatch[1]);
            }
            return this.cleanPersonName(fromAdiSoyadi);
        }

        const fromUets = this.findValueAfterLabel(rawText, ["uets hesap sahibi"], { maxNextLines: 1 });
        if (fromUets) {
            return this.cleanPersonName(fromUets);
        }

        const candidates = this.extractNameCandidates(normalized.text);
        const best = this.pickBestName(candidates);
        if (best) return best;

        return this.cleanPersonName(this.extractByLabel(rawText, ["muhatap", "tebliğ olunan", "teblig olunan", "alıcı", "alici", "ad soyad"]));
    }

    private findKurumAdSoyad(rawText: string): string | null {
        const value = this.findValueAfterLabel(rawText, ["adı ve soyadı", "adi ve soyadi"], { maxNextLines: 2 });
        const cleaned = this.cleanText(value || "");
        if (!cleaned) return null;

        const kurumIbareleri = ["şirket", "sirket", "limited", "ltd", "anonim", "a.ş", "a.s", "sanayi", "ticaret", "belediyesi"];
        const isLikelyCompany = kurumIbareleri.some((x) => cleaned.toLocaleLowerCase("tr-TR").includes(x));

        if (isLikelyCompany) {
            return cleaned;
        }
        return this.extractByLabel(rawText, ["kurum adı", "kurum adi", "firma", "şirket", "sirket", "ünvan", "unvan"]);
    }

    private findAdres(rawText: string): string | null {
        return this.findAddress(rawText);
    }

    private findTebligatIcerigi(rawText: string): string | null {
        const lines = rawText.split(/\n+/).map((x) => x.trim()).filter(Boolean);
        const hit = lines.find((x) => /bu zarfta.+vardir|bu zarfta.+vardır/i.test(x));
        if (hit) return hit;
        return rawText ? rawText.slice(0, 500) : null;
    }

    private findDosyaEsasNo(rawText: string): string | null {
        const regexes = [
            /dosya\s*(esas)?\s*no[:\s-]*([0-9]{4}\/[0-9]{1,10})/i,
            /\b([0-9]{4}\/[0-9]{1,10})\b/
        ];

        for (const rgx of regexes) {
            const m = rawText.match(rgx);
            if (m && (m[2] || m[1])) {
                return (m[2] || m[1]).trim();
            }
        }
        return null;
    }

    private findBirimAdi(rawText: string): string | null {
        const lines = this.getCleanLines(rawText);
        const birimLine = lines.find((x) => /genel\s+icra\s+dairesi|icra\s+dairesi/i.test(x));
        if (birimLine) {
            return birimLine;
        }
        return this.extractByLabel(rawText, ["birim", "icra dairesi", "mahkeme", "adliye"]);
    }

    private detectKurum(adSoyad: string | null, kurumAd: string | null): boolean {
        if (kurumAd && kurumAd.trim().length > 0) return true;
        const value = (adSoyad || "").toLocaleLowerCase("tr-TR");
        const kurumIbareleri = ["a.ş", "a.s", "anonim", "limited", "ltd", "şti", "sirket", "kurumu", "belediyesi"];
        return kurumIbareleri.some((x) => value.includes(x));
    }

    private getCleanLines(rawText: string): string[] {
        return rawText
            .split(/\n+/)
            .map((x) => this.cleanText(x))
            .filter(Boolean);
    }

    private findLabelLineIndex(lines: string[], labels: string[]): number {
        return lines.findIndex((line) => {
            const normalized = line.toLocaleLowerCase("tr-TR");
            return labels.some((label) => normalized.includes(label.toLocaleLowerCase("tr-TR")));
        });
    }

    private findValueAfterLabel(
        rawText: string,
        labels: string[],
        options: { maxNextLines?: number } = {}
    ): string | null {
        const maxNextLines = options.maxNextLines ?? 1;
        const lines = this.getCleanLines(rawText);
        const index = this.findLabelLineIndex(lines, labels);
        if (index < 0) return null;

        const line = lines[index];
        const normalizedLine = line.toLocaleLowerCase("tr-TR");
        let first = "";
        for (const label of labels) {
            const labelIdx = normalizedLine.indexOf(label.toLocaleLowerCase("tr-TR"));
            if (labelIdx >= 0) {
                const afterLabel = line.slice(labelIdx);
                const sepIdx = afterLabel.indexOf(":");
                if (sepIdx >= 0) {
                    first = this.cleanText(afterLabel.slice(sepIdx + 1));
                } else {
                    first = this.cleanText(afterLabel.slice(label.length));
                }
                if (first) break;
            }
        }
        const values: string[] = [];

        if (first) values.push(first);

        for (let i = 1; i <= maxNextLines; i++) {
            const next = lines[index + i];
            if (!next) break;
            if (this.isStopLine(next)) break;
            values.push(next);
        }

        const joined = this.cleanText(values.join(" "));
        if (joined) return joined;

        // Fallback: tek satıra sıkışmış metinlerde label sonrası değeri yakala.
        const text = this.cleanText(rawText);
        const labelGroup = labels.map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
        const rgx = new RegExp(`(?:${labelGroup})\\s*[:\\-]\\s*([^\\n]+)`, "iu");
        const m = text.match(rgx);
        if (m?.[1]) {
            return this.cleanText(m[1]);
        }
        return null;
    }

    private isStopLine(line: string): boolean {
        const low = line.toLocaleLowerCase("tr-TR");
        const stopPatterns = [
            /^no[:\s]/,
            /^mühür/,
            /^tebliğ/,
            /^kep /,
            /^buradan katlayiniz/,
            /^bu zarfta/,
            /^dosya no[:\s]/,
            /^örnek no[:\s]/,
            /^- /
        ];
        return stopPatterns.some((re) => re.test(low));
    }

    private looksLikeAddress(line: string): boolean {
        const low = line.toLocaleLowerCase("tr-TR");
        const tokens = ["mah", "mh", "mahallesi", "sk", "sokak", "cad", "caddesi", "bulvar", "blv", "no:", "kapı", "kat", "/", "ankara", "istanbul", "çankaya", "kadıköy", "kadikoy"];
        return tokens.some((t) => low.includes(t));
    }

    private cleanPersonName(value: string | null): string | null {
        if (!value) return null;
        let out = this.cleanText(value);
        // Etiket sonrası gelen açıklamaları kes.
        out = out.replace(/\b(uets\s+hesap\s+sahibi|vekili|vekil)\b.*$/i, " ");
        out = out.replace(/\b(mühür|muhur|imza|dosya\s*no|icra\s*no|e-?tebligat)\b.*$/i, " ");
        out = out.replace(/\[[^\]]*\]/g, " ");
        out = out.replace(/\([^)]*\)/g, " ");
        out = out.replace(/\btc[:\s]*[0-9*]+/gi, " ");
        out = out.replace(/\btckn[:\s]*[0-9*]+/gi, " ");
        out = out.replace(/\btc\s*kimlik\s*no[:\s]*[0-9*]+/gi, " ");
        out = out.replace(/\buets\s*hesap\s*sahibi[:\s]*/gi, " ");
        out = out.replace(/\bvekil[iı]?\s+av\.?\s+/gi, " ");
        out = out.replace(/\bav\.?\s+/gi, " ");
        out = out.replace(/[/\-–].*$/, " ");
        out = out.replace(/^alacaklı\s+/i, "");
        out = out.replace(/^alacakli\s+/i, "");
        out = out.replace(/^borçlu\s+/i, "");
        out = out.replace(/^borclu\s+/i, "");
        out = out.replace(/^muhatap\s+/i, "");
        out = out.replace(/^alıcı\s+/i, "");
        out = out.replace(/^alici\s+/i, "");

        const bannedTokens = new Set([
            "t.c", "tc", "t.c.", "adliye", "icra", "daire", "dairesi",
            "mahkeme", "müdürlüğü", "mudurlugu", "mühür", "muhur", "imza",
            "ankara", "istanbul", "izmir", "teblig", "tebliğ", "mazbatasi",
            "örnek", "odeme", "ödeme", "emri", "takip", "dayanak", "belge",
            "vardir", "vardır", "dosya", "no"
        ]);

        out = out
            .split(/\s+/)
            .filter((w) => {
                const low = w.toLocaleLowerCase("tr-TR").replace(/\./g, "");
                return low && !bannedTokens.has(low);
            })
            .join(" ");

        out = out.replace(/[^A-ZÇĞİÖŞÜa-zçğıöşü'\s]/g, " ");
        out = this.cleanText(out);

        const words = out.split(/\s+/).filter(Boolean);
        if (words.length < 2) return null;
        return out || null;
    }

    private isWeakText(text: string): boolean {
        return text.replace(/\s+/g, "").length < 30;
    }

    private cleanText(value: string): string {
        return value.replace(/\s+/g, " ").trim();
    }

    private pushLines(target: string[], text: string): void {
        if (!text) return;
        const lines = text
            .split(/\n+/)
            .map((x) => this.cleanText(x))
            .filter((x) => x.length >= 2);

        for (const line of lines) {
            if (!target.includes(line)) {
                target.push(line);
            }
        }
    }

    private static ensurePdfWorkerConfigured(): void {
        if (FrontendTebligatPdfParser.isWorkerConfigured) {
            return;
        }

        try {
            // Webpack/Vue CLI altında worker asset path'i üretmek için entry import edilir.
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const workerEntry = require("pdfjs-dist/legacy/build/pdf.worker.entry");
            const workerSrc = workerEntry?.default || workerEntry;
            if (workerSrc) {
                (pdfjsLib as any).GlobalWorkerOptions.workerSrc = workerSrc;
            }
        } catch (e) {
            console.warn("pdf.js workerSrc ayarlanamadı, fake worker denenebilir.", e);
        } finally {
            FrontendTebligatPdfParser.isWorkerConfigured = true;
        }
    }

    private normalizeText(raw: string): { text: string; lower: string } {
        const normalized = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
        const lines = normalized
            .split("\n")
            .map((line) => this.cleanText(line))
            .filter((line) => line.length > 0)
            .filter((line) => !this.isNoiseLine(line));

        const text = lines.join("\n");
        return { text, lower: text.toLocaleLowerCase("tr-TR") };
    }

    private isNoiseLine(line: string): boolean {
        const low = line.toLocaleLowerCase("tr-TR");
        const noisePatterns = [
            /buradan\s+katlay/i,
            /sms\.uyap/i,
            /vatandas\.uyap/i,
            /tebligat\s+bilgilendirme/i,
            /kep\s+hesab/i,
            /kep\s+adres/i,
            /^-?\s*barkod/i,
            /\bwww\./i,
            /https?:\/\//i,
            /\*{2,}.*\*{2,}/,
            /^\*?[0-9A-Z\-\/+%. ]{12,}\*?$/i,
            /tckimlikno\s+kisiselstandart/i
        ];
        return noisePatterns.some((re) => re.test(low));
    }

    private extractNameCandidates(rawText: string): string[] {
        const normalized = this.normalizeText(rawText);
        const text = normalized.text;
        const candidates: string[] = [];

        const labelRegexes = [
            /(?:adı\s+ve\s+soyadı|adi\s+ve\s+soyadi|adı\s+soyadı|adi\s+soyadi)\s*[:\-]\s*([^\n]+)/giu,
            /(?:adı\s*soyadı|adi\s*soyadi)\s*[:\-]\s*([^\n]+)/giu
        ];

        for (const rgx of labelRegexes) {
            let match: RegExpExecArray | null;
            while ((match = rgx.exec(text)) !== null) {
                if (match[1]) candidates.push(match[1]);
            }
        }

        const tcBeforeRegex = /([A-ZÇĞİÖŞÜa-zçğıöşü\s']{5,})\s*(?:\([^)]*\)\s*)?(?:T\.?C\.?|TCKN|TC\s*kimlik)\s*[:\-]/giu;
        let tcMatch: RegExpExecArray | null;
        while ((tcMatch = tcBeforeRegex.exec(text)) !== null) {
            if (tcMatch[1]) candidates.push(tcMatch[1]);
        }

        const borcluRegex = /\b(?:borçlu|borclu|muhatap)\s+([A-ZÇĞİÖŞÜa-zçğıöşü\s']{5,})/giu;
        let borcluMatch: RegExpExecArray | null;
        while ((borcluMatch = borcluRegex.exec(text)) !== null) {
            if (borcluMatch[1]) candidates.push(borcluMatch[1]);
        }

        return candidates
            .map((x) => this.cleanPersonName(x))
            .filter((x): x is string => !!x);
    }

    private pickBestName(cands: string[]): string | null {
        if (!cands.length) return null;
        let best: { value: string; score: number } | null = null;
        for (const cand of cands) {
            const words = cand.split(/\s+/).filter(Boolean);
            let score = 0;
            if (words.length >= 2) score += 5;
            if (words.length >= 3) score += 2;
            if (cand.length >= 10) score += 2;
            if (cand === cand.toLocaleUpperCase("tr-TR")) score += 1;
            if (this.containsAddressKeywords(cand)) score -= 10;
            if (!best || score > best.score) best = { value: cand, score };
        }
        return best ? best.value : null;
    }

    private findTckn(rawText: string): { value: string | null; isMasked: boolean } {
        const normalized = this.normalizeText(rawText);
        const text = normalized.text;

        const regexes = [
            /(t\.?c\.?\s*kimlik\s*no|tckn|tc)\s*[:\-]?\s*([0-9*]{5,})/gi
        ];

        const candidates: string[] = [];
        for (const rgx of regexes) {
            let match: RegExpExecArray | null;
            while ((match = rgx.exec(text)) !== null) {
                if (match[2]) candidates.push(match[2]);
            }
        }

        const cleaned = candidates
            .map((x) => x.replace(/[^0-9*]/g, ""))
            .filter((x) => x.length >= 5);

        if (!cleaned.length) return { value: null, isMasked: false };

        let best = cleaned[0];
        for (const c of cleaned) {
            if (c.length === 11 && best.length !== 11) {
                best = c;
            } else if (c.length > best.length && best.length !== 11) {
                best = c;
            }
        }

        return { value: best, isMasked: best.includes("*") };
    }

    private findAddress(rawText: string): string | null {
        const normalized = this.normalizeText(rawText);
        const lines = normalized.text.split("\n").map((x) => this.cleanText(x)).filter(Boolean);

        const labelCandidates = this.extractAddressByLabel(lines, ["adres", "adresi", "tebligat adresi", "muhatap adresi", "tebliğ adresi", "teblig adresi"]);
        if (labelCandidates.length > 0) {
            const best = this.pickBestAddress(labelCandidates);
            if (best) return best;
        }

        const fallbackCandidates: string[] = [];
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (this.looksLikeAddress(line)) {
                fallbackCandidates.push(line);
                const next = lines[i + 1];
                if (next && !this.isAddressStopLine(next) && !this.isNoiseLine(next)) {
                    fallbackCandidates.push(this.cleanText(`${line} ${next}`));
                }
            }
        }

        return this.pickBestAddress(fallbackCandidates);
    }

    private extractAddressByLabel(lines: string[], labels: string[]): string[] {
        const candidates: string[] = [];
        const labelIdx = this.findLabelLineIndex(lines, labels);
        if (labelIdx < 0) return candidates;

        const line = lines[labelIdx];
        const idx = line.indexOf(":");
        const first = idx >= 0 ? this.cleanText(line.slice(idx + 1)) : "";
        const values: string[] = [];
        if (first) values.push(first);

        for (let i = 1; i <= 3; i++) {
            const next = lines[labelIdx + i];
            if (!next) break;
            if (this.isAddressStopLine(next)) break;
            values.push(next);
        }

        if (values.length > 0) {
            candidates.push(this.cleanText(values.join(" ")));
        }
        return candidates;
    }

    private isAddressStopLine(line: string): boolean {
        const low = line.toLocaleLowerCase("tr-TR");
        const stopPatterns = [
            /^no[:\s]/,
            /^mühür/,
            /^tebliğ/,
            /^kep /,
            /^buradan katlayiniz/,
            /^bu zarfta/,
            /^dosya no[:\s]/,
            /^örnek no[:\s]/,
            /^- /,
            /^barkod/,
            /^uets/,
            /^sms\./,
            /^t\.?c\./,
            /^ankara/
        ];
        return stopPatterns.some((re) => re.test(low));
    }

    private pickBestAddress(cands: string[]): string | null {
        const cleaned = cands
            .map((x) => x.replace(/\([^)]*\)/g, " "))
            .map((x) => this.cleanText(x))
            .filter((x) => x.length >= 10)
            .filter((x) => !this.isNoiseLine(x));

        if (!cleaned.length) return null;

        let best: { value: string; score: number } | null = null;
        for (const cand of cleaned) {
            const keywordCount = this.countAddressKeywords(cand);
            let score = cand.length + keywordCount * 8;
            if (cand.includes("/")) score += 5;
            if (/tebliğ|teblig|mazbata|örnek|ödeme|emri/i.test(cand)) score -= 15;
            if (!best || score > best.score) best = { value: cand, score };
        }
        return best ? best.value : null;
    }

    private containsAddressKeywords(value: string): boolean {
        return this.countAddressKeywords(value) > 0;
    }

    private countAddressKeywords(value: string): number {
        const low = value.toLocaleLowerCase("tr-TR");
        const tokens = [
            "mah", "mahallesi", "mh",
            "sk", "sokak", "cad", "caddesi", "bulvar", "blv",
            "no:", "no", "kapı", "kapi", "kat", "daire", "iç", "ic",
            "apt", "apt.", "site", "blok", "ilce", "il", "/",
            "cad.", "sk.", "mah."
        ];
        return tokens.filter((t) => low.includes(t)).length;
    }

    private parseTebligat(rawText: string): {
        adSoyad: string | null;
        tckn: string | null;
        tcknMasked: boolean;
        address: string | null;
    } {
        const normalized = this.normalizeText(rawText);
        const adSoyad = this.findAdSoyad(normalized.text);
        const tcknInfo = this.findTckn(normalized.text);
        const address = this.findAddress(normalized.text);
        return {
            adSoyad,
            tckn: tcknInfo.value,
            tcknMasked: tcknInfo.isMasked,
            address
        };
    }
}
